import { useState, useMemo } from 'react';
import { Boxes, Plus } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { formatINR } from '@/shared/utils/currency';
import { packSummary, ratePerBaseUnit } from '@/shared/utils/ratePerBaseUnit';
import { useMasterItems } from '@/features/master-data/hooks/useMasters';
import { useProductsByTaxonomy, useProductFacets, type ProductHierarchyItem } from '../hooks/useCatalogApi';

interface ProductHierarchySelectorProps {
  onSelectProduct: (product: ProductHierarchyItem, quantity: number, customRate?: number) => void;
  onCancel?: () => void;
  currencyCode?: string;
  conversionRate?: number;
}

const getPackageBadgeColor = (type?: string) => {
  switch (type) {
    case 'Box': return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'Bucket': return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'Can': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'Barrel': return 'bg-purple-50 text-purple-700 border-purple-200';
    default: return 'bg-slate-50 text-slate-700 border-slate-200';
  }
};

const getPackageIcon = (type?: string) => {
  switch (type) {
    case 'Box': return '📦';
    case 'Bucket': return '🪣';
    case 'Can': return '🥫';
    case 'Barrel': return '🛢️';
    default: return '📦';
  }
};

export function ProductHierarchySelector({
  onSelectProduct,
  onCancel,
  currencyCode = 'INR',
  conversionRate = 1,
}: ProductHierarchySelectorProps) {
  const [divisionId, setDivisionId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [gradeId, setGradeId] = useState('');
  const [selectorId, setSelectorId] = useState('');
  const [packSizeId, setPackSizeId] = useState('');

  // Division → Category → Group is a parentId chain in master_items, so each
  // level's options are simply the children of the level above.
  const { data: divisions = [] } = useMasterItems('product_division', undefined, true);
  const { data: categories = [] } = useMasterItems('product_category', divisionId || '', true);
  const { data: groups = [] } = useMasterItems('product_group', categoryId || '', true);
  // Grade/Selector/Pack Size options are scoped to what actually exists for
  // the selected Group (each further narrows by the one before it) — not the
  // full system-wide master list, which would offer nonsense like "NLGI 2"
  // while browsing Coolant.
  const { data: facets } = useProductFacets(divisionId, groupId, gradeId, categoryId, selectorId);
  const grades = facets?.grades ?? [];
  const selectors = facets?.selectors ?? [];
  const packSizes = facets?.packSizes ?? [];

  const { data: items = [], isLoading } = useProductsByTaxonomy(divisionId, groupId, gradeId, packSizeId, categoryId, selectorId);

  const divisionsList = useMemo(() => {
    if (!Array.isArray(divisions)) return [];
    const seen = new Set<string>();
    return divisions
      .filter((d) => d && d.isActive !== false)
      .filter((d) => {
        const norm = (d.name || '').trim().toUpperCase();
        if (!norm || seen.has(norm)) return false;
        seen.add(norm);
        return true;
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [divisions]);

  const categoriesList = useMemo(() => {
    if (!Array.isArray(categories)) return [];
    const seen = new Set<string>();
    return categories
      .filter((c) => c && c.isActive !== false)
      .filter((c) => {
        const norm = (c.name || '').trim().toUpperCase();
        if (!norm || seen.has(norm)) return false;
        seen.add(norm);
        return true;
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [categories]);

  const groupsList = useMemo(() => {
    if (!Array.isArray(groups)) return [];
    const seen = new Set<string>();
    return groups
      .filter((g) => g && g.isActive !== false)
      .filter((g) => {
        const norm = (g.name || '').trim().toUpperCase();
        if (!norm || seen.has(norm)) return false;
        seen.add(norm);
        return true;
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [groups]);
  const gradesList = Array.isArray(grades) ? grades.filter(Boolean) : [];
  const selectorsList = Array.isArray(selectors) ? selectors.filter(Boolean) : [];
  const packSizesList = Array.isArray(packSizes) ? packSizes.filter(Boolean) : [];
  const itemsList = Array.isArray(items) ? items.filter(Boolean) : [];

  // Each level invalidates everything below it — a Group picked under the old
  // Category does not exist under the new one.
  const setDivision = (id: string) => { setDivisionId(id); setCategoryId(''); setGroupId(''); setGradeId(''); setSelectorId(''); setPackSizeId(''); };
  const setCategory = (id: string) => { setCategoryId(id); setGroupId(''); setGradeId(''); setSelectorId(''); setPackSizeId(''); };
  const setGroup = (id: string) => { setGroupId(id); setGradeId(''); setSelectorId(''); setPackSizeId(''); };
  // Changing Grade invalidates a Selector/Pack Size that may not exist for it.
  const setGrade = (id: string) => { setGradeId(id); setSelectorId(''); setPackSizeId(''); };
  // Changing Selector (API/OEM rating) invalidates a Pack Size that may not
  // exist for that rating — two products differing only by CF vs SL often
  // only ship in different packs.
  const setSelector = (id: string) => { setSelectorId(id); setPackSizeId(''); };

  // Clicking a product adds it straight to the line items table — quantity
  // and rate are edited there afterward (the table already has editable Qty/
  // Rate columns), rather than in a separate "Ready to add" review step here.
  // No custom rate is passed, so the line picks up the product's own selling
  // price/MRP automatically.
  const handlePick = (item: ProductHierarchyItem) => {
    onSelectProduct(item, 1, undefined);
  };

  return (
    <div className="flex flex-col gap-5 p-5 bg-surface rounded-2xl border border-outline shadow-sm">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between border-b border-outline/60 pb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-xl bg-primary/10 text-primary">
            <Boxes className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-bold text-on-surface text-body-base">RBM Oil Product Selector</h3>
            <p className="text-body-xs text-on-surface-variant">
              Division → Category → Group → Grade → Selector → Pack Size — click a product to add it
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone="primary">Official Catalog</Badge>
          {onCancel && (
            <Button type="button" variant="outline" size="sm" onClick={onCancel}>
              Close
            </Button>
          )}
        </div>
      </div>

      {/* ── The 6 taxonomy dropdowns ──────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="flex flex-col gap-1.5">
          <label className="text-body-xs font-bold text-on-surface">
            Division <span className="text-error">*</span>
          </label>
          <Select
            value={divisionId}
            onValueChange={setDivision}
            options={[
              { value: '', label: '— Select Division —' },
              ...divisionsList.map((d) => ({ value: String(d?._id || ''), label: String(d?.name || '') })),
            ]}
            className="w-full text-body-sm font-medium"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-body-xs font-bold text-on-surface">
            Category <span className="text-error">*</span>
          </label>
          <Select
            value={categoryId}
            onValueChange={setCategory}
            disabled={!divisionId}
            options={[
              { value: '', label: divisionId ? '— Select Category —' : 'Select a Division first' },
              ...categoriesList.map((c) => ({ value: String(c?._id || ''), label: String(c?.name || '') })),
            ]}
            className="w-full text-body-sm font-medium"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-body-xs font-bold text-on-surface">
            Group <span className="text-error">*</span>
          </label>
          <Select
            value={groupId}
            onValueChange={setGroup}
            disabled={!categoryId}
            options={[
              { value: '', label: categoryId ? '— Select Group —' : 'Select a Category first' },
              ...groupsList.map((g) => ({ value: String(g?._id || ''), label: String(g?.name || '') })),
            ]}
            className="w-full text-body-sm font-medium"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-body-xs font-bold text-on-surface">Grade</label>
          <Select
            value={gradeId}
            onValueChange={setGrade}
            disabled={!groupId}
            options={[
              { value: '', label: groupId ? '— Any Grade —' : 'Select a Group first' },
              ...gradesList.map((g) => ({ value: String(g?._id || ''), label: String(g?.name || '') })),
            ]}
            className="w-full text-body-sm font-medium"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-body-xs font-bold text-on-surface">Selector (API/OEM)</label>
          <Select
            value={selectorId}
            onValueChange={setSelector}
            disabled={!groupId}
            options={[
              { value: '', label: groupId ? '— Any Selector —' : 'Select a Group first' },
              ...selectorsList.map((s) => ({ value: String(s?._id || ''), label: String(s?.name || '') })),
            ]}
            className="w-full text-body-sm font-medium"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-body-xs font-bold text-on-surface">Pack Size</label>
          <Select
            value={packSizeId}
            onValueChange={setPackSizeId}
            disabled={!groupId}
            options={[
              { value: '', label: groupId ? '— Any Pack Size —' : 'Select a Group first' },
              ...packSizesList.map((p) => ({ value: String(p?._id || ''), label: String(p?.name || '') })),
            ]}
            className="w-full text-body-sm font-medium"
          />
        </div>
      </div>

      {/* ── Matching products ──────────────────────────────────────────────── */}
      {divisionId && groupId && (
        <div className="flex flex-col gap-1.5 animate-fadeIn">
          <label className="text-body-xs font-bold text-on-surface flex items-center justify-between">
            <span>Matching Products</span>
            <span className="text-[11px] font-normal text-on-surface-variant">{itemsList.length} found</span>
          </label>
          {isLoading ? (
            <div className="flex items-center justify-center p-6">
              <Spinner />
            </div>
          ) : itemsList.length === 0 ? (
            <div className="p-4 rounded-xl border border-dashed border-outline text-center text-body-sm text-on-surface-variant">
              No products found for this Division/Category/Group/Grade/Pack Size combination.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
              {itemsList.map((item) => {
                const rate = item.sellingPrice || item.mrp || 0;
                return (
                  <button
                    type="button"
                    key={item._id}
                    onClick={() => handlePick(item)}
                    className="group text-left p-3 rounded-xl border border-outline bg-surface hover:border-primary hover:bg-primary/5 hover:ring-2 hover:ring-primary/20 transition flex flex-col gap-1.5"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-body-sm font-bold text-on-surface flex items-center gap-1.5">
                        <span>{getPackageIcon(item.packagingType)}</span>
                        {item.name}
                      </span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${getPackageBadgeColor(item.packagingType)}`}>
                        {item.packagingType}
                      </span>
                    </div>
                    <div className="text-body-xs text-on-surface-variant font-mono">
                      SKU: {item.sku} {item.grade && <>• Grade: {item.grade}</>}
                      {item.selector && <> • {item.selector}</>}
                    </div>
                    <div className="flex items-center justify-between text-body-xs text-on-surface-variant">
                      <span>
                        {packSummary(item) || item.packSize}
                      </span>
                      <span className="font-semibold text-emerald-600">In Stock: {item.currentStock}</span>
                    </div>
                    <div className="flex items-center justify-between pt-1.5 mt-0.5 border-t border-outline/60">
                      {(() => {
                        const isForeign = currencyCode && currencyCode !== 'INR' && Number(conversionRate) > 0;
                        const rateFx = Number(conversionRate) || 1;
                        const sym = currencyCode === 'USD' ? '$' : currencyCode === 'EUR' ? '€' : currencyCode === 'GBP' ? '£' : `${currencyCode} `;
                        const docRate = isForeign && rate ? Math.round((rate / rateFx) * 100) / 100 : rate;

                        return (
                          <div className="flex flex-col">
                            {rate ? (
                              isForeign ? (
                                <>
                                  <span className="text-body-sm font-black text-primary">
                                    {sym}{docRate.toFixed(2)}
                                    <span className="ml-1.5 text-[11px] font-normal text-on-surface-variant">
                                      (≈ {formatINR(rate)})
                                    </span>
                                  </span>
                                  <span className="text-[10px] font-semibold text-emerald-700">
                                    {sym}{ratePerBaseUnit(docRate, item)}
                                  </span>
                                </>
                              ) : (
                                <span className="text-body-sm font-black text-on-surface">
                                  {formatINR(rate)}
                                  {ratePerBaseUnit(rate, item) && (
                                    <span className="ml-1 text-[11px] font-bold text-emerald-700">
                                      {ratePerBaseUnit(rate, item)}
                                    </span>
                                  )}
                                </span>
                              )
                            ) : (
                              <span className="text-body-sm font-medium text-on-surface-variant">No price set</span>
                            )}
                          </div>
                        );
                      })()}
                      <span className="flex items-center gap-1 text-body-xs font-bold text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                        <Plus className="h-3.5 w-3.5" /> Add
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ProductHierarchySelector;
